import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { tierLabel } from '../../config/pricing';
import ThemeToggle from '../../components/ThemeToggle';

function MasterAdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = localStorage.getItem('masterAdminToken');
  const user = JSON.parse(localStorage.getItem('masterAdminUser') || '{}');

  useEffect(() => {
    if (!token) {
      navigate('/master-admin/login');
      return;
    }
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/master-admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401 || response.status === 403) {
        localStorage.removeItem('masterAdminToken');
        navigate('/master-admin/login');
        return;
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('masterAdminToken');
    localStorage.removeItem('masterAdminUser');
    navigate('/master-admin/login');
  };

  if (loading) {
    return (
      <div className="admin-page master-admin-page">
        <div className="admin-loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page master-admin-page">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <h1>DeedStack Platform Admin</h1>
        </div>
        <div className="admin-header-right">
          <span className="admin-user-name">{user.firstName} {user.lastName}</span>
          <ThemeToggle />
          <button onClick={handleLogout} className="btn btn-secondary btn-small">
            Logout
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="admin-nav">
        <Link to="/master-admin/dashboard" className="admin-nav-link active">Dashboard</Link>
        <Link to="/master-admin/tenants" className="admin-nav-link">Tenants</Link>
        <Link to="/master-admin/calendar" className="admin-nav-link">Calendar</Link>
        <Link to="/master-admin/emails" className="admin-nav-link">Emails</Link>
        <Link to="/master-admin/billing" className="admin-nav-link">Billing</Link>
        <Link to="/master-admin/financials" className="admin-nav-link">Financials</Link>
        <Link to="/master-admin/settings" className="admin-nav-link">Settings</Link>
      </nav>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-container">
          <div className="admin-section">
            <h2>Platform Overview</h2>

          {error && <div className="error-message">{error}</div>}

          {stats && (
            <>
              {/* Key Metrics */}
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{stats.totalTenants}</div>
                  <div className="stat-label">Total Tenants</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">${stats.monthlyRevenue.toLocaleString()}</div>
                  <div className="stat-label">Monthly Revenue</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.recentSignups}</div>
                  <div className="stat-label">New (30 days)</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{stats.usage.totalLoans}</div>
                  <div className="stat-label">Active Loans</div>
                </div>
              </div>

              {/* Tier Breakdown */}
              <div className="admin-card" style={{ marginTop: '2rem' }}>
                <h3>Tenants by Tier</h3>
                <div className="tier-breakdown">
                  {/* Labels come from config/pricing.js. This block used to
                      hardcode Starter at $49 while the billing page and every
                      real charge used $69. */}
                  <div className="tier-item">
                    <span className="tier-label">{tierLabel('free_trial')}</span>
                    <span className="tier-value">{stats.tierBreakdown.free_trial || 0}</span>
                  </div>
                  <div className="tier-item">
                    <span className="tier-label">{tierLabel('starter')}</span>
                    <span className="tier-value">{stats.tierBreakdown.starter || 0}</span>
                  </div>
                  <div className="tier-item">
                    <span className="tier-label">{tierLabel('pro')}</span>
                    <span className="tier-value">{stats.tierBreakdown.pro || 0}</span>
                  </div>
                  <div className="tier-item">
                    <span className="tier-label">{tierLabel('enterprise')}</span>
                    <span className="tier-value">{stats.tierBreakdown.enterprise || 0}</span>
                  </div>
                </div>
              </div>

              {/* Status Breakdown */}
              <div className="admin-card" style={{ marginTop: '1rem' }}>
                <h3>Tenants by Status</h3>
                <div className="tier-breakdown">
                  <div className="tier-item">
                    <span className="tier-label">Trial</span>
                    <span className="tier-value">{stats.statusBreakdown.trial || 0}</span>
                  </div>
                  <div className="tier-item">
                    <span className="tier-label">Active</span>
                    <span className="tier-value">{stats.statusBreakdown.active || 0}</span>
                  </div>
                  <div className="tier-item">
                    <span className="tier-label">Past Due</span>
                    <span className="tier-value">{stats.statusBreakdown.past_due || 0}</span>
                  </div>
                  <div className="tier-item">
                    <span className="tier-label">Suspended</span>
                    <span className="tier-value">{stats.statusBreakdown.suspended || 0}</span>
                  </div>
                  <div className="tier-item">
                    <span className="tier-label">Cancelled</span>
                    <span className="tier-value">{stats.statusBreakdown.cancelled || 0}</span>
                  </div>
                </div>
              </div>

              {/* Platform Usage */}
              <div className="admin-card" style={{ marginTop: '1rem' }}>
                <h3>Platform Usage (All Tenants)</h3>
                <div className="tier-breakdown">
                  <div className="tier-item">
                    <span className="tier-label">Total Properties</span>
                    <span className="tier-value">{stats.usage.totalProperties}</span>
                  </div>
                  <div className="tier-item">
                    <span className="tier-label">Active Loans</span>
                    <span className="tier-value">{stats.usage.totalLoans}</span>
                  </div>
                  <div className="tier-item">
                    <span className="tier-label">Admin Users</span>
                    <span className="tier-value">{stats.usage.totalUsers}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Quick Actions */}
          <div className="admin-card" style={{ marginTop: '2rem' }}>
            <h3>Quick Actions</h3>
            <div className="quick-actions">
              <Link to="/master-admin/tenants" className="btn btn-primary">
                View All Tenants
              </Link>
              <Link to="/master-admin/tenants/new" className="btn btn-secondary">
                Add New Tenant
              </Link>
            </div>
          </div>
        </div>
        </div>
      </main>
    </div>
  );
}

export default MasterAdminDashboard;
